import { type ApolloClient, useApolloClient } from '@apollo/client'
import { FetchViewSearchSqlResultDocument } from '@salescore/client-api'
import { useMe } from '@salescore/client-common'
import type { ChangeHistory, ViewRecordFieldChange } from '@salescore/client-redux'
import type { ViewQueryField, ViewQueryList } from '@salescore/core'
import { logger } from '@salescore/frontend-common'
import { Array, pipe, Record } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import Mustache from 'mustache'
import { useCallback, useMemo } from 'react'

import type { RecordChange } from '../../../rsheet/util/updateViewRecords'

/**
 * フィールドに設定されたコールバックを、 `newChange` をもとに発火させる
 */
export const useEvaluateFieldCallbacks = (
  query: ViewQueryList | undefined,
): ((newChange: ChangeHistory) => Promise<RecordChange[]>) => {
  const me = useMe()
  const client = useApolloClient()
  const fields = useMemo(() => query?.fields ?? [], [query])
  const fieldsWithCallback = useMemo(() => fields.filter((x) => (x.meta.callbacks ?? []).length > 0), [fields])

  return useCallback(
    async (newChange) => {
      if (fieldsWithCallback.length === 0) {
        return []
      }
      const fieldChanges = newChange.changes.flatMap((x) => x.fieldChanges)
      const results = await Promise.all(
        fieldChanges.map(
          async (fieldChange) =>
            await evaluateCallbacksForFieldChange(me.organization.id, client, fields, fieldsWithCallback, fieldChange),
        ),
      )
      return results.flat()
    },
    [client, fields, fieldsWithCallback, me.organization.id],
  )
}

// eslint-disable-next-line @typescript-eslint/max-params
async function evaluateCallbacksForFieldChange(
  organizationId: string,
  client: ApolloClient<unknown>,
  fields: ViewQueryField[],
  fieldsWithCallback: ViewQueryField[],
  fieldChange: ViewRecordFieldChange['fieldChanges'][0],
): Promise<RecordChange[]> {
  const changedField = fieldsWithCallback.find((x) => x.name === fieldChange.fieldName)
  if (changedField === undefined) {
    return []
  }

  const xs = (changedField.meta.callbacks ?? []).map(async (callback) => {
    logger.debug(`[evaluateCallback] callback`, callback)
    const rawSql = callback.sql
    const sql = Mustache.render(rawSql, { id: fieldChange.id, value: fieldChange.value })

    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: searchを流用している
    const result = await client.query({
      query: FetchViewSearchSqlResultDocument,
      variables: {
        organizationId,
        sql,
      },
    })

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const rows = (result.data.viewSearchSqlResult.rows ?? []) as Array<Record<string, unknown>>
    const [row] = rows
    if (row === undefined) {
      return []
    }

    const recordChanges = pipe(
      row,
      Record.collect((fieldName, value): RecordChange | undefined => {
        const changedByCallbackField = fields.find((x) => x.name === fieldName)
        if (changedByCallbackField === undefined) {
          return undefined
        }
        return {
          rowIndex: fieldChange.rowIndex,
          innerRowIndex: fieldChange.innerRowIndex,
          nodePath: changedByCallbackField.nodePath,
          isNestedColumn: changedByCallbackField.nodePath.length > 1,
          field: changedByCallbackField,
          value,
          label: undefined,
        }
      }),
      Array.filter(isNotNullish),
    )
    return recordChanges
  })
  const results = await Promise.all(xs)
  return results.flat()
}
